import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DIAL_CODES, splitPhone } from "@/components/dashboard/PhoneCard";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Building2, FolderLock, ExternalLink, Phone, UserRound, Briefcase, Layers, Clock } from "lucide-react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { SECTORS, COUNTRIES, STAGES, SERVICE_TYPES } from "@/lib/constants";
import { CompanyTeamCard } from "@/components/dashboard/CompanyTeamCard";
import { toast } from "sonner";

function MultiSelectBadges({ options, selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isActive = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onToggle(opt)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              isActive
                ? "bg-[color:var(--brand-primary)] text-white border-[color:var(--brand-primary)]"
                : "bg-transparent border-border hover:bg-accent"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

/** A white panel with a titled header — the profile page's building block. */
function Section({ icon: Icon, title, description, children, testId }) {
  return (
    <Card className="rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid={testId}>
      <div className="flex items-start gap-3 pb-4 mb-5 border-b border-border">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-base md:text-lg font-semibold leading-tight">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </Card>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function DashboardProfileTab() {
  const { t, i18n } = useTranslation();
  const { user, fetchMe } = useAuth();
  const [form, setForm] = useState(null);
  const [access, setAccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [phone, setPhone] = useState(() => splitPhone(user?.phone));
  const [typeInfo, setTypeInfo] = useState(null);
  const [industry, setIndustry] = useState("");
  const [btSlug, setBtSlug] = useState("");
  const [btReason, setBtReason] = useState("");

  const loadType = async () => {
    const res = await client.get("/my-company/type");
    setTypeInfo(res.data);
    setIndustry(res.data?.company?.industry || "");
    setBtSlug(res.data?.business_type_slug || "");
    setBtReason("");
  };

  useEffect(() => {
    client.get("/profile/me").then((res) => {
      setForm(res.data.profile || {});
      setAccess(res.data.access || { company: null, data_rooms: [] });
    });
    loadType().catch(() => setTypeInfo(null));
  }, []);

  useEffect(() => { setPhone(splitPhone(user?.phone)); }, [user?.phone]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const toggleMulti = (field, val) => {
    setForm((f) => {
      const list = (f[field] || []).includes(val) ? f[field].filter((x) => x !== val) : [...(f[field] || []), val];
      return { ...f, [field]: list };
    });
  };

  // One save for the whole page: profile fields, the contact number and the
  // company type all go together so the member never hunts for a second button.
  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await client.put("/profile/me", form);
      await client.patch("/me/contact", { phone_country: phone.country, phone: phone.number });
      let typeChanged = false;
      if (typeInfo?.linked) {
        if (industry !== (typeInfo.company?.industry || "")) {
          await client.patch("/my-company/type", { industry });
        }
        if (btSlug && btSlug !== (typeInfo.business_type_slug || "")) {
          await client.post("/my-company/business-type", { slug: btSlug, reason: btReason });
          typeChanged = true;
        }
        await loadType();
      }
      if (fetchMe) await fetchMe();
      toast.success(typeChanged ? t("companyType.changedToast") : t("dashboard.profileUpdated"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const role = user?.role;
  const hasAccessPanel = !!(access && (access.company || (access.data_rooms || []).length > 0));
  const companyField = (
    <Field label={t("onboarding.companyName")} wide>
      {access?.company ? (
        <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5" data-testid="profile-linked-company">
          <span className="inline-flex h-6 items-center rounded-full bg-primary/15 px-2 text-[10px] uppercase tracking-widest text-primary font-semibold">
            {t("profilePage.linked", { defaultValue: "Linked" })}
          </span>
          <span className="text-sm font-medium truncate">{access.company.name}</span>
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground" data-testid="profile-company-admin-only">
          {t("dashboard.companyAdminOnly")}
        </div>
      )}
    </Field>
  );

  return (
    <form onSubmit={handleSave} data-testid="dashboard-profile-tab">
      <div className="mb-5 sm:mb-6">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {t(`roles.${role}`, { defaultValue: role })}
        </p>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1" data-testid="profile-page-title">
          {t("dashboard.profile")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
          {t("profilePage.subtitle", { defaultValue: "How the ecosystem sees you. Everything on this page saves together." })}
        </p>
      </div>

      <div className={`grid gap-5 lg:items-start ${hasAccessPanel ? "lg:grid-cols-3" : "max-w-4xl"}`}>
        <div className={`space-y-5 ${hasAccessPanel ? "lg:col-span-2" : ""}`}>
          <Section
            icon={UserRound}
            title={t("profilePage.identity", { defaultValue: "You" })}
            description={t("profilePage.identityHint", { defaultValue: "Your public headline, bio and where you're based." })}
            testId="profile-section-identity"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t("onboarding.headline")} wide>
                <Input className="rounded-xl" value={form.headline || ""} onChange={(e) => update("headline", e.target.value)} data-testid="profile-edit-headline" />
              </Field>
              <Field label={t("onboarding.bio")} wide>
                <Textarea className="rounded-xl min-h-[110px]" value={form.bio || ""} onChange={(e) => update("bio", e.target.value)} data-testid="profile-edit-bio" />
              </Field>
              <Field label={t("profileFields.country", { defaultValue: "Country" })}>
                <Select value={form.country || ""} onValueChange={(v) => update("country", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="profile-edit-country"><SelectValue placeholder={t("profileFields.countryPlaceholder", { defaultValue: "Select your country" })} /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section
            icon={Phone}
            title={t("phone.title")}
            description={t("phone.hint")}
            testId="profile-section-contact"
          >
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="sm:w-[150px]">
                <Select value={phone.country} onValueChange={(v) => setPhone((p) => ({ ...p, country: v }))}>
                  <SelectTrigger className="rounded-xl h-10" data-testid="profile-phone-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {DIAL_CODES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                value={phone.number}
                onChange={(e) => setPhone((p) => ({ ...p, number: e.target.value.replace(/[^0-9\s-]/g, "") }))}
                placeholder="5X XXX XXXX"
                dir="ltr"
                inputMode="tel"
                className="rounded-xl h-10 flex-1 font-mono"
                data-testid="profile-phone-number"
              />
            </div>
          </Section>

          {typeInfo?.linked && (
            <Section
              icon={Layers}
              title={t("companyType.title")}
              description={t("companyType.hint")}
              testId="profile-section-company-type"
            >
              {typeInfo.pending_request && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2.5" data-testid="company-type-pending">
                  <Clock className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-700">{t("companyType.pendingTitle")}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t("companyType.pendingBody", {
                        type: (i18n.language?.startsWith("ar") && typeInfo.business_type?.name_ar) || typeInfo.business_type?.name_en || typeInfo.business_type_slug,
                      })}
                    </p>
                  </div>
                </div>
              )}
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t("companyType.industry")}>
                  <Select value={industry} onValueChange={setIndustry}>
                    <SelectTrigger className="rounded-xl" data-testid="company-type-industry">
                      <SelectValue placeholder={t("companyType.industryPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("companyType.businessType")}>
                  <Select value={btSlug} onValueChange={setBtSlug}>
                    <SelectTrigger className="rounded-xl" data-testid="company-type-business-type">
                      <SelectValue placeholder={t("companyType.businessTypePlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {(typeInfo.business_types || []).map((bt) => (
                        <SelectItem key={bt.slug} value={bt.slug}>
                          {(i18n.language?.startsWith("ar") && bt.name_ar) || bt.name_en || bt.slug}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {btSlug && btSlug !== (typeInfo.business_type_slug || "") && (
                  <Field label={t("companyType.reason")} wide>
                    <Input
                      className="rounded-xl"
                      value={btReason}
                      onChange={(e) => setBtReason(e.target.value)}
                      placeholder={t("companyType.reasonPlaceholder")}
                      data-testid="company-type-reason"
                    />
                  </Field>
                )}
              </div>
            </Section>
          )}

          {role === "founder" && (
            <Section
              icon={Briefcase}
              title={t("profilePage.startup", { defaultValue: "Your startup" })}
              testId="profile-section-role"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                {companyField}
                <Field label={t("onboarding.sectors")} wide>
                  <MultiSelectBadges options={SECTORS} selected={form.sectors || []} onToggle={(v) => toggleMulti("sectors", v)} />
                </Field>
                <Field label={t("onboarding.stage")}>
                  <Select value={form.stage || ""} onValueChange={(v) => update("stage", v)}>
                    <SelectTrigger className="rounded-xl" data-testid="profile-edit-stage"><SelectValue placeholder={t("onboarding.stage")} /></SelectTrigger>
                    <SelectContent>
                      {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("onboarding.pitchDocUrl")}>
                  <Input className="rounded-xl" value={form.pitch_doc_url || ""} onChange={(e) => update("pitch_doc_url", e.target.value)} data-testid="profile-edit-pitch" />
                </Field>
                <Field label={t("onboarding.website")} wide>
                  <Input className="rounded-xl" value={form.website || ""} onChange={(e) => update("website", e.target.value)} data-testid="profile-edit-website" />
                </Field>
              </div>
            </Section>
          )}

          {role === "mentor" && (
            <Section
              icon={Briefcase}
              title={t("profilePage.mentoring", { defaultValue: "Your mentoring" })}
              testId="profile-section-role"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t("onboarding.expertise")} wide>
                  <MultiSelectBadges options={SECTORS} selected={form.expertise || []} onToggle={(v) => toggleMulti("expertise", v)} />
                </Field>
                <Field label={t("onboarding.yearsExperience")}>
                  <Input type="number" className="rounded-xl" value={form.years_experience || ""} onChange={(e) => update("years_experience", parseInt(e.target.value || 0, 10))} data-testid="profile-edit-years" />
                </Field>
                <Field label={t("onboarding.linkedinUrl")}>
                  <Input className="rounded-xl" value={form.linkedin_url || ""} onChange={(e) => update("linkedin_url", e.target.value)} data-testid="profile-edit-linkedin" />
                </Field>
              </div>
            </Section>
          )}

          {role === "enabler" && (
            <Section
              icon={Briefcase}
              title={t("profilePage.services", { defaultValue: "Your services" })}
              testId="profile-section-role"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                {companyField}
                <Field label={t("onboarding.serviceType")}>
                  <Select value={form.service_type || ""} onValueChange={(v) => update("service_type", v)}>
                    <SelectTrigger className="rounded-xl" data-testid="profile-edit-service-type"><SelectValue placeholder={t("onboarding.serviceType")} /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("onboarding.servicesOffered")} wide>
                  <MultiSelectBadges options={SERVICE_TYPES} selected={form.services || []} onToggle={(v) => toggleMulti("services", v)} />
                </Field>
              </div>
            </Section>
          )}

          {role === "investor" && (
            <Section
              icon={Briefcase}
              title={t("profilePage.investing", { defaultValue: "Your investing" })}
              testId="profile-section-role"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={t("onboarding.investorFocus")} wide>
                  <MultiSelectBadges options={SECTORS} selected={form.investor_focus || []} onToggle={(v) => toggleMulti("investor_focus", v)} />
                </Field>
                <Field label={t("onboarding.ticketSize")}>
                  <Input className="rounded-xl" value={form.ticket_size || ""} onChange={(e) => update("ticket_size", e.target.value)} data-testid="profile-edit-ticket" />
                </Field>
              </div>
            </Section>
          )}

          <CompanyTeamCard />

        </div>

        {/* Admin-managed access lives beside the editable fields on desktop so            members can see what they've been granted without scrolling. */}
        <div className={`space-y-5 lg:sticky lg:top-24 ${hasAccessPanel ? "" : "hidden"}`}>
          {hasAccessPanel ? (
            <Card className="rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:p-5 space-y-4" data-testid="profile-access-panel">
              <h3 className="font-display text-sm font-semibold">
                {t("profilePage.accessTitle", { defaultValue: "Managed by the team" })}
              </h3>
              {access.company && (
                <div className="flex items-start gap-3" data-testid="profile-linked-company-card">
                  <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <Building2 className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">
                      {t("profilePage.linkedCompany", { defaultValue: "Linked company" })}
                    </div>
                    <div className="text-sm font-semibold truncate">{access.company.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {[access.company.industry, access.company.region, access.company.stage].filter(Boolean).join(" · ") || "—"}
                    </div>
                  </div>
                </div>
              )}
              {(access.data_rooms || []).length > 0 && (
                <div data-testid="profile-data-rooms-list">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary font-semibold mb-1.5">
                    <FolderLock className="h-3 w-3" />
                    {t("profilePage.roomAccess", { defaultValue: "Data room access" })} ({access.data_rooms.length})
                  </div>
                  <div className="space-y-1.5">
                    {access.data_rooms.map((r) => (
                      <Link
                        key={r.room_id}
                        to={`/data-rooms/${r.room_id}`}
                        className="flex items-center justify-between gap-2 rounded-lg bg-background/70 border border-border hover:border-primary/40 px-2.5 py-1.5 transition-colors"
                        data-testid={`profile-data-room-${r.room_id}`}
                      >
                        <div className="min-w-0">
                          <div className="text-xs font-medium truncate">{r.company_name || "Room"}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{t("dataRoom.roleLabel", { defaultValue: "Role" })} · {r.role}</div>
                        </div>
                        <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0 rtl-flip" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ) : null}

        </div>
      </div>

      <div className="sticky bottom-16 sm:bottom-0 z-10 mt-5 -mx-1 px-1 pt-4 pb-3 bg-gradient-to-t from-background via-background to-transparent">
        <Button
          type="submit"
          className="rounded-xl bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-white gap-2 w-full sm:w-auto sm:px-10"
          disabled={saving}
          data-testid="profile-save-button"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
